import gsap from '../../../gsap-public/esm/index.js';
import ScrollTrigger from '../../../gsap-public/esm/ScrollTrigger.js';
import SplitText from '../../../gsap-public/esm/SplitText.js';

import ScrollManager from './ScrollManager.js';
import WebGLManager from '../webgl/WebGLManager.js';
import PageTransitions from './PageTransitions.js';
import Preloader from '../utils/preloader.js';

import HeroAnimation from '../animations/HeroAnimation.js';
import ScrollAnimations from '../animations/ScrollAnimations.js';
import PortfolioGrid from '../components/PortfolioGrid.js';
import ContactForm from '../components/ContactForm.js';
import Cursor from '../components/Cursor.js';
import ThemeToggle from '../components/ThemeToggle.js';

gsap.registerPlugin(ScrollTrigger, SplitText);

// Helper function for timestamped logs
const log = (emoji, message, ...args) => {
  const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
  console.log(`[${timestamp}] ${emoji} ${message}`, ...args);
};

class App {
  constructor() {
    this.modules = {};
    this.isReady = false;
  }

  async init() {
    log('🚀', 'App: Initializing GenEdu Portfolio...');

    // 1. Preload critical assets (skip if navigating between pages)
    const isInternalNav = sessionStorage.getItem('genedu-navigating');
    if (isInternalNav) {
      log('🚀', 'App: Skipping preloader (internal navigation)');
      sessionStorage.removeItem('genedu-navigating');
      // Hide preloader immediately if it exists
      const preloaderEl = document.getElementById('preloader');
      if (preloaderEl) preloaderEl.style.display = 'none';
    } else {
      log('🚀', 'App: Step 1 - Preloading assets');
      await this.preload();
      log('🚀', 'App: Preload complete');
    }

    // 2. Initialize core systems
    log('🚀', 'App: Step 2 - Initializing core systems');
    this.initCore();
    log('🚀', 'App: Core systems initialized');

    // 3. Initialize page-specific modules
    log('🚀', 'App: Step 3 - Initializing page modules');
    this.initPage();
    log('🚀', 'App: Page modules initialized');

    // 4. Start animations
    log('🚀', 'App: Step 4 - Starting animations');
    this.start();

    log('✨', 'App: Ready!');
    this.isReady = true;
  }

  async preload() {
    const preloader = new Preloader();
    // Add critical assets here if needed
    await preloader.load([]);

    // Add a small delay after preloader to ensure everything is ready
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  initCore() {
    // Smooth scroll
    this.modules.scrollManager = new ScrollManager();

    // WebGL
    const canvas = document.getElementById('webgl-canvas');
    if (canvas) {
      this.modules.webglManager = new WebGLManager(canvas);
    }

    // Custom cursor
    this.modules.cursor = new Cursor();

    // Theme toggle (disabled — locked to light mode for now)
    // this.modules.themeToggle = new ThemeToggle();

    // Page transitions (for case study navigation)
    this.modules.pageTransitions = new PageTransitions();
  }

  initPage() {
    const page = document.body.dataset.page || 'home';

    if (page === 'home') {
      this.initHomePage();
    } else if (page === 'case-study') {
      this.initCaseStudyPage();
    }
  }

  initHomePage() {
    log('📄', 'App.initHomePage: Initializing home page...');

    // Hero animation - DISABLED to keep text always visible
    const heroTitle = document.querySelector('[data-hero-title]');
    log('📄', 'App.initHomePage: Hero title element:', heroTitle ? 'Found' : 'Not found');
    log('📄', 'App.initHomePage: HeroAnimation DISABLED - text will remain static and visible');
    // DISABLED - causes text to disappear
    // if (heroTitle) {
    //   log('📄', 'App.initHomePage: Creating HeroAnimation');
    //   this.modules.heroAnimation = new HeroAnimation();
    //   log('📄', 'App.initHomePage: HeroAnimation created');
    // } else {
    //   log('⚠️', 'App.initHomePage: Hero title not found, skipping hero animation');
    // }

    // Hero logo is now static - no animation needed

    // Scroll-triggered animations
    log('📄', 'App.initHomePage: Creating ScrollAnimations');
    this.modules.scrollAnimations = new ScrollAnimations();

    // Portfolio grid interactions
    const portfolioGrid = document.querySelector('[data-portfolio-grid]');
    if (portfolioGrid) {
      log('📄', 'App.initHomePage: Creating PortfolioGrid');
      this.modules.portfolioGrid = new PortfolioGrid();
    }

    // Contact form
    const contactForm = document.querySelector('[data-contact-form]');
    if (contactForm) {
      log('📄', 'App.initHomePage: Creating ContactForm');
      this.modules.contactForm = new ContactForm();
    }

    // Bind navigation smooth scroll
    log('📄', 'App.initHomePage: Binding navigation');
    this.bindNavigation();
    log('📄', 'App.initHomePage: Home page initialization complete');
  }

  initCaseStudyPage() {
    log('📄', 'App.initCaseStudyPage: Initializing case study page...');

    // Case study title animation
    this.animateCaseStudyEntry();

    // Scroll-reveal animations for case study sections
    this.animateCaseStudySections();

    log('📄', 'App.initCaseStudyPage: Case study page initialization complete');
  }

  start() {
    log('🎬', 'App.start: Beginning start sequence');

    // Start WebGL render loop (if any scenes are enabled)
    if (this.modules.webglManager) {
      log('🎬', 'App.start: Starting WebGL manager');
      this.modules.webglManager.start();
    }

    // Enable scroll immediately (no hero animation delay)
    log('🎬', 'App.start: Enabling scroll immediately (hero animation disabled)');
    if (this.modules.scrollManager) {
      this.modules.scrollManager.start();
    }

    // Refresh ScrollTrigger after everything is initialized and DOM is settled
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
      log('🎬', 'App.start: ScrollTrigger refreshed');
    });

    // Set navigation flag for future page transitions
    this.setNavigationFlag();
  }

  setNavigationFlag() {
    // Mark internal navigation so the next page can skip the preloader
    document.querySelectorAll('a[href]').forEach(link => {
      const href = link.getAttribute('href');
      if (href && (href.startsWith('/') || href.endsWith('.html')) && !href.startsWith('//') && !href.startsWith('http')) {
        link.addEventListener('click', () => {
          sessionStorage.setItem('genedu-navigating', 'true');
        });
      }
    });
  }

  bindNavigation() {
    const navLinks = document.querySelectorAll('.nav__link');

    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const target = link.getAttribute('href');

        if (target.startsWith('#')) {
          this.modules.scrollManager.scrollTo(target);
        }
      });
    });

    // Scroll to work button
    const heroCTA = document.querySelector('[data-hero-cta]');
    if (heroCTA) {
      heroCTA.addEventListener('click', () => {
        this.modules.scrollManager.scrollTo('#work');
      });
    }
  }

  animateCaseStudyEntry() {
    const title = document.querySelector('[data-case-title]');
    if (!title) return;

    const splitTitle = new SplitText(title, { type: 'chars, words' });

    gsap.from(splitTitle.chars, {
      opacity: 0,
      y: 100,
      rotateX: -90,
      stagger: 0.02,
      duration: 1,
      ease: 'back.out(1.7)',
      delay: 0.3
    });

    // Fade in meta items
    const metaItems = document.querySelectorAll('.case-study__meta-item');
    gsap.from(metaItems, {
      opacity: 0,
      y: 20,
      stagger: 0.1,
      duration: 0.6,
      ease: 'power3.out',
      delay: 0.6
    });

    // Reveal hero image
    const heroImage = document.querySelector('.case-study__hero-image');
    if (heroImage) {
      gsap.from(heroImage, {
        opacity: 0,
        y: 40,
        scale: 0.98,
        duration: 1,
        ease: 'power3.out',
        delay: 0.8
      });
    }
  }

  animateCaseStudySections() {
    // Wait for next frame so DOM is fully laid out
    requestAnimationFrame(() => this._setupCaseStudyScrollAnimations());
  }

  _setupCaseStudyScrollAnimations() {
    // Animate each section as it scrolls into view
    const sections = document.querySelectorAll('.case-study__section');

    sections.forEach((section) => {
      const content = section.querySelector('.case-study__content');
      const sidebar = section.querySelector('.case-study__sidebar');
      const heading = content?.querySelector('h2');
      const paragraphs = content?.querySelectorAll('p');
      const lists = content?.querySelectorAll('.case-study__list');
      const subsections = content?.querySelectorAll('.case-study__subsection-title');

      // Animate heading
      if (heading) {
        gsap.from(heading, {
          opacity: 0,
          y: 30,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        });
      }

      // Animate paragraphs and lists with stagger
      const contentItems = [];
      if (subsections) contentItems.push(...subsections);
      if (paragraphs) contentItems.push(...paragraphs);
      if (lists) contentItems.push(...lists);

      if (contentItems.length > 0) {
        gsap.from(contentItems, {
          opacity: 0,
          y: 25,
          stagger: 0.08,
          duration: 0.7,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          }
        });
      }

      // Animate sidebar
      if (sidebar) {
        gsap.from(sidebar, {
          opacity: 0,
          x: 30,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          }
        });
      }
    });

    // Animate the "Next Project" section
    const nextSection = document.querySelector('.case-study__next');
    if (nextSection) {
      gsap.from(nextSection.querySelector('.next-project'), {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: nextSection,
          start: 'top 85%',
          toggleActions: 'play none none reverse'
        }
      });
    }

    // Refresh ScrollTrigger after all case study animations are set up
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
  }
}

export default App;
